class PurchaseOptions extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      product: "[data-main-product]",
      sellingPlanGroups: "[data-selling-plan-group-selector]",
      sellingPlans: "[data-selling-plan-selector]",
      sellingPlansWrapper: "[data-selling-plans-wrapper]"
    };
    this.productBase = false;

    this.productSelector = this.closest(
      this.selectors.product
    ).querySelector("product-selector");

    if (!this.productSelector) {
      this.productSelector = this.closest(this.selectors.product);
      this.productBase = true;
    }

    this.productForm = this.closest(
      this.selectors.product
    ).querySelector("product-form");
  }

  connectedCallback() {
    this.setHandlers();
    // Signyl (Matt 07-10): one-time purchase is the default — the theme's
    // stock state. No client-side plan flip on load.
  }

  setHandlers() {
    // Signyl: delegated on the persistent element, because renderProductInfo()
    // replaces the inner DOM of the options region and listeners bound to the
    // child selects die after the first re-render.
    if (this.dataset.signylDelegated) return;
    this.dataset.signylDelegated = "1";
    this.addEventListener("change", event => {
      if (!window.__signylApplyingDefault) window.__signylPurchaseTouched = true;
      if (event.target.matches(this.selectors.sellingPlanGroups)) {
        if (event.target.value === "one-time") {
          // Signyl: deterministic one-time handling. The stock branch round-trips
          // through a section re-render that can silently no-op and re-select a
          // plan; one-time needs no server state, so handle it fully here.
          this.querySelectorAll(this.selectors.sellingPlans).forEach(select => {
            select.value = "";
            Array.from(select.options).forEach(option =>
              option.toggleAttribute("selected", option.value === "")
            );
          });
          const wrapper = this.querySelector(this.selectors.sellingPlansWrapper);
          if (wrapper) {
            wrapper.style.maxHeight = "0";
            wrapper.classList.add("is-hidden");
          }
          this.closest(".product__purchase-options")
            ?.querySelector(".product__purchase--info")
            ?.classList.add("hidden");
          const params = new URLSearchParams(window.location.search);
          params.delete("selling_plan");
          const query = params.toString();
          window.history.replaceState(
            {},
            "",
            `${window.location.pathname}${query ? "?" + query : ""}`
          );
          this.productForm?.removeAttribute("data-has-selling-plan");
          this.signylSyncPurchaseUi(false);
          this.updateProductSelector();
          return;
        }
        this.onSellingGroupChange(event);
        this.signylSyncPurchaseUi(true);
        return;
      }
      if (event.target.matches(this.selectors.sellingPlans)) {
        event.target
          .closest("dropdown-input")
          ?.classList?.remove("has-error");
        this.updateProductSelector(event.target.value);
      }
    });
  }

  // Signyl (Ben 07-10 image 64, reworked after Matt's freeze report): the app
  // rewrites the submit label on its own change listener, which can run after
  // ours. NO MutationObserver here — an observer can ping-pong with the app's
  // and lock the main thread. Plain delayed re-writes win the last word.
  signylSyncPurchaseUi(subscribed) {
    this.dataset.signylPlanState = subscribed ? "subscription" : "one-time";
    const scope = this.closest(".product-grid__item--content") || document;
    const submit = scope.querySelector(".product-form__submit");
    if (submit) {
      const want = subscribed ? "Add subscription to cart" : "Add to cart";
      const write = () => {
        if (this.dataset.signylPlanState !== (subscribed ? "subscription" : "one-time")) return;
        const el = submit.querySelector(":scope > span") || submit;
        if (el.textContent.trim() !== want) el.textContent = want;
      };
      write();
      setTimeout(write, 250);
      setTimeout(write, 800);
    }
    scope.querySelectorAll("*").forEach(el => {
      if (el.children.length === 0 && (el.textContent || "").includes("deferred, subscription, or recurring")) {
        let box = el;
        for (let i = 0; i < 4 && box.parentElement; i++) {
          if ((box.parentElement.textContent || "").trim().length > box.textContent.trim().length + 80) break;
          box = box.parentElement;
        }
        box.style.display = subscribed ? "" : "none";
      }
    });
  }

  onSellingGroupChange(event) {
    if (event.target.value == "one-time") {
      Array.from(
        this.querySelectorAll(this.selectors.sellingPlans)
      ).map(select => {
        select.value = "";
      });

      const sellingPlansWrapper = this.querySelector(
        this.selectors.sellingPlansWrapper
      );
      sellingPlansWrapper.style.maxHeight = "0";
      sellingPlansWrapper.classList.add("is-hidden");


      const sellingPlanInfo = this.closest('.product__purchase-options').querySelector('.product__purchase--info');
      sellingPlanInfo.classList.add("hidden");

      this.updateProductSelector();
      return;
    }

    const sellingPlansWrapperId = event.target.querySelector(
      `option[value="${event.target.value}"]`
    ).dataset.id;

    const sellingPlansWrapper = this.querySelector(
      this.selectors.sellingPlansWrapper
    );
    sellingPlansWrapper
      .querySelectorAll(`[data-id]`)
      .forEach(sellingGroupWrapper => {
        sellingGroupWrapper.classList.toggle(
          "is-hidden",
          sellingGroupWrapper.dataset.id !== sellingPlansWrapperId
        );
      });
    sellingPlansWrapper.style.maxHeight = `${sellingPlansWrapper.scrollHeight}px`;
    sellingPlansWrapper.classList.remove("is-hidden");
  }

  isOneTimePurchase() {
    return (
      this.querySelector('[name="selling_plan_group"]').value ===
      "one-time"
    );
  }

  showError() {
    const sellingPlanWrappers = this.querySelectorAll(
      this.selectors.sellingPlansWrapper
    );
    const activeSellingPlanWrapper = [...sellingPlanWrappers].filter(
      sellingPlanWrapper =>
        !sellingPlanWrapper.classList.contains("is-hidden")
    )[0];
    activeSellingPlanWrapper
      .querySelectorAll("dropdown-input")
      .forEach(dropdownInput =>
        dropdownInput.classList.add("has-error")
      );
  }

  updateProductSelector(sellingPlanId) {
    if (this.productBase) {
      this.updateProductBase(sellingPlanId);
      return;
    }

    if (!this.productSelector.currentVariant) {
      this.productSelector.updateVariant();
    }

    this.productSelector.updateURL(sellingPlanId);
    this.productSelector.renderProductInfo();
    if (
      this.productSelector.currentVariant.available &&
      sellingPlanId
    ) {
      this.productForm.setAttribute("data-has-selling-plan", "true");
      this.productForm.toggleAddButton(false, "");
      return;
    }

    this.productForm.removeAttribute("data-has-selling-plan", true);
    this.productForm.toggleAddButton(false, "");
  }

  updateProductBase(sellingPlanId) {
    const selectedVariant = this.productSelector.getSelectedVariant(
      this.productSelector
    );

    this.productSelector.updateSellingPlanURL(
      sellingPlanId,
      selectedVariant.id
    );
    this.renderPurchaseInfo(selectedVariant.id);

    if (selectedVariant.available && sellingPlanId) {
      this.productForm.setAttribute("data-has-selling-plan", "true");
      this.productForm.toggleSubmitButton(false, "");
      return;
    }

    this.productForm.removeAttribute("data-has-selling-plan");
    this.productForm.toggleSubmitButton(false, "");
  }

  renderPurchaseInfo(selectedVariantId) {
    const params = new URLSearchParams(window.location.search);
    params[params.has("variant") ? "set" : "append"](
      "variant",
      selectedVariantId
    );

    // Do not let a slower response restore a previously selected plan/variant.
    const requestedPlan = params.get('selling_plan');
    const requestedVariant = params.get('variant');
    fetch(`${this.productSelector.dataset.url}?${params.toString()}`)
      .then(response => response.text())
      .then(responseText => {
        const currentParams = new URLSearchParams(window.location.search);
        if (currentParams.get('selling_plan') !== requestedPlan ||
            currentParams.get('variant') !== requestedVariant) return;
        const html = new DOMParser().parseFromString(
          responseText,
          "text/html"
        );
        [
          `#Product-Purchase-Options-${this.productSelector.dataset.section}`,
          `#ProductPurchaseInfo-${this.productSelector.dataset.section}`,
          `#price-${this.productSelector.dataset.section}`
        ].map(
          liveRegionSelector => {
            const destination = document.querySelector(
              liveRegionSelector
            );
            const source = html.querySelector(liveRegionSelector);
            if (destination && source) {
              destination.classList.remove("hidden");
              destination.innerHTML = source.innerHTML;
            }
          }
        );
      })
      .catch(error => {
        console.error(error);
      });
  }
}

customElements.define('purchase-options', PurchaseOptions);
